import { IN_VIVO_AI_SHORT } from '@/components/Agents/SendValidator/utils/SendAiUse';
import { _isNil } from '@/littledash';
import type { FC, ReactNode } from 'react';
import styles from './SendResults.module.scss';

/**
 * The regions of a SEND report, each filled by its own component. A region not filled, or filled
 * with an empty list, renders nothing, so a part of the report not built yet leaves no gap and no
 * placeholder. Pass nothing rather than a component that renders nothing, which still takes a region.
 */
export interface SendReportSlots {
  /** Files, rows and whether Benchling In Vivo AI took part. */
  header: ReactNode;
  /** Problems with the input, and whether the upload is in the validator's scope. */
  scope?: ReactNode;
  /** How the upload fared against the published rules. */
  ruleChecks?: ReactNode;
  /** The rules not evaluated, each with why, and everything a limit left out. */
  notEvaluated?: ReactNode;
  /** One section per uploaded file. */
  files?: ReactNode;
  /** The checks across files, grouped by the domains they span. */
  crossDomain?: ReactNode;
  /** Benchling In Vivo AI's notes, in their own region and never inside a check's. */
  aiNotes?: ReactNode;
}

/** The order the regions read in, top to bottom. Input and scope come early: they change how the rest reads. */
const SEND_REPORT_SLOT_ORDER: ReadonlyArray<keyof SendReportSlots> = [
  'header',
  'scope',
  'ruleChecks',
  'notEvaluated',
  'files',
  'crossDomain',
  'aiNotes',
];

const SLOT_LABELS: Record<keyof SendReportSlots, string> = {
  header: 'Run',
  scope: 'Input and scope',
  ruleChecks: 'Rule checks',
  notEvaluated: 'Not evaluated and left out',
  files: 'Files',
  crossDomain: 'Cross-domain checks',
  aiNotes: `${IN_VIVO_AI_SHORT} notes`,
};

/** A region with nothing in it: absent, switched off, or an empty list, such as a run with no files. */
const isUnfilled = (content: ReactNode): boolean =>
  _isNil(content) || content === false || (Array.isArray(content) && content.length === 0);

interface SendReportLayoutProps {
  slots: SendReportSlots;
  testId?: string;
}

/**
 * Lays a SEND report's regions out in one fixed order, each a labelled region, so every part of
 * the report is placed by this one component and a later region fills its slot without moving
 * anything else.
 */
const SendReportLayout: FC<SendReportLayoutProps> = ({ slots, testId = 'invivo-send-validator__report' }) => (
  <div className="flex flex-column" data-test-component="SendReportLayout" data-testid={testId}>
    {SEND_REPORT_SLOT_ORDER.map((slot) =>
      isUnfilled(slots[slot]) ? null : (
        <section
          key={slot}
          className={styles.reportSlot}
          aria-label={SLOT_LABELS[slot]}
          data-test-element="report-slot"
          data-test-key={slot}
        >
          {slots[slot]}
        </section>
      )
    )}
  </div>
);

export default SendReportLayout;
